import type {
  CutDirection,
  Note,
  ObstacleType,
  TargetAppearance,
  TargetSize,
  TargetSpeed,
} from "./beatmap";
import rhythm01 from "../beatmaps/survival/survival-01.json";
import rhythm02 from "../beatmaps/survival/survival-02.json";
import rhythm03 from "../beatmaps/survival/survival-03.json";
import rhythm04 from "../beatmaps/survival/survival-04.json";
import rhythm05 from "../beatmaps/survival/survival-05.json";
import rhythm06 from "../beatmaps/survival/survival-06.json";

export interface SurvivalRhythm {
  bpm: number;
  duration: number;
  beats: number[];
}

export interface SurvivalTrack {
  id: string;
  title: string;
  src: string;
  rhythm: SurvivalRhythm;
}

export const SURVIVAL_TRACKS: SurvivalTrack[] = [
  {
    id: "blade-line-ii",
    title: "Find the Blade Line II",
    src: "assets/audio/survival-01.mp3",
    rhythm: rhythm01,
  },
  {
    id: "honest-cut-ii",
    title: "The Honest Cut II",
    src: "assets/audio/survival-02.mp3",
    rhythm: rhythm02,
  },
  {
    id: "diagonal-ii",
    title: "The Diagonal II",
    src: "assets/audio/survival-03.mp3",
    rhythm: rhythm03,
  },
  {
    id: "graduation-ii",
    title: "The Graduation II",
    src: "assets/audio/survival-04.mp3",
    rhythm: rhythm04,
  },
  {
    id: "diagonal-iii",
    title: "The Diagonal III",
    src: "assets/audio/survival-05.mp3",
    rhythm: rhythm05,
  },
  {
    id: "graduation-iii",
    title: "The Graduation III",
    src: "assets/audio/survival-06.mp3",
    rhythm: rhythm06,
  },
];

export const SURVIVAL_MISS_LIMIT = 10;
export const SURVIVAL_POOL_SIZE = 12;
export const SURVIVAL_MIN_GAP_SECONDS = 0.3;
export const SURVIVAL_AUDIO_CLIP_SECONDS = 40;

const TRACK_STAGE_SECONDS = 36;
const TRACK_STAGE_SCORE = 12_000;
const FORGIVING_SECONDS = 18;
const FORGIVING_INTENSITY = FORGIVING_SECONDS / TRACK_STAGE_SECONDS;
const HIGH_INTENSITY_TRACKS = [3, 4, 5];

export interface SurvivalDifficulty {
  normalized: number;
  // Baseline profile for standard blue targets. Precision and reaction
  // pressure are sampled independently through the chances below.
  size: TargetSize;
  speed: TargetSpeed;
  allowedDirections: CutDirection[];
  beatStride: number;
  subdivisionChance: number;
  fastChance: number;
  smallChance: number;
  smallFastChance: number;
  obstacleChance: number;
}

const CARDINAL_DIRECTIONS: CutDirection[] = ["left", "right", "up", "down"];
const DIAGONAL_DIRECTIONS: CutDirection[] = [
  "up-left",
  "up-right",
  "down-left",
  "down-right",
];
const ALL_DIRECTIONS = [...CARDINAL_DIRECTIONS, ...DIAGONAL_DIRECTIONS];

export function survivalStage(elapsedSeconds: number, score: number): number {
  return Math.floor(survivalIntensity(elapsedSeconds, score));
}

export function trackIndexForStage(stage: number): number {
  if (stage < SURVIVAL_TRACKS.length) return Math.max(0, stage);
  return HIGH_INTENSITY_TRACKS[
    (stage - SURVIVAL_TRACKS.length) % HIGH_INTENSITY_TRACKS.length
  ];
}

// Intensity is deliberately unbounded. The normalized levers approach their
// launch maxima smoothly, while the small post-peak creep keeps score pressure
// rising during repeated high-intensity tracks.
export function survivalIntensity(
  elapsedSeconds: number,
  score: number,
): number {
  return Math.max(
    Math.max(0, elapsedSeconds) / TRACK_STAGE_SECONDS,
    Math.max(0, score) / TRACK_STAGE_SCORE,
  );
}

export function normalizedIntensity(intensity: number): number {
  const activeIntensity = Math.max(0, intensity - FORGIVING_INTENSITY);
  return 1 - Math.exp(-activeIntensity * 0.52);
}

export function difficultyForIntensity(intensity: number): SurvivalDifficulty {
  const normalized = normalizedIntensity(intensity);
  const size: TargetSize = normalized < 0.3 ? "large" : "medium";
  const speed: TargetSpeed = normalized < 0.25 ? "slow" : "base";
  const allowedDirections: CutDirection[] =
    normalized < 0.42
      ? CARDINAL_DIRECTIONS
      : normalized < 0.68
        ? ([...CARDINAL_DIRECTIONS, "up-left", "down-right"] as CutDirection[])
        : ALL_DIRECTIONS;
  const beatStride =
    normalized < 0.12 ? 4 : normalized < 0.32 ? 3 : normalized < 0.58 ? 2 : 1;

  return {
    normalized,
    size,
    speed,
    allowedDirections,
    beatStride,
    subdivisionChance:
      normalized < 0.66 ? 0 : Math.min(0.72, (normalized - 0.66) * 2.1),
    fastChance: normalized < 0.5 ? 0 : Math.min(0.45, (normalized - 0.5) * 0.9),
    smallChance:
      normalized < 0.58 ? 0 : Math.min(0.45, (normalized - 0.58) * 1.4),
    smallFastChance:
      normalized < 0.82 ? 0 : Math.min(0.08, (normalized - 0.82) * 0.45),
    obstacleChance:
      normalized < 0.38 ? 0 : Math.min(0.18, (normalized - 0.38) * 0.32),
  };
}

export interface SurvivalCompositionState {
  direction: CutDirection;
  positionKey: number;
}

export type SurvivalBeatEvent =
  | { kind: "target"; trackTime: number }
  | { kind: "obstacle"; trackTime: number; type: ObstacleType };

export function generateSurvivalBeatEvents(
  beats: number[],
  beatIndex: number,
  difficulty: SurvivalDifficulty,
  random: () => number = Math.random,
): SurvivalBeatEvent[] {
  const beatTime = beats[beatIndex];
  if (beatTime == null || beatIndex % difficulty.beatStride !== 0) return [];

  if (random() < difficulty.obstacleChance) {
    return [
      {
        kind: "obstacle",
        trackTime: beatTime,
        type: pickSurvivalObstacle(random),
      },
    ];
  }

  const events: SurvivalBeatEvent[] = [{ kind: "target", trackTime: beatTime }];
  const nextBeat = beats[beatIndex + 1];
  if (
    nextBeat != null &&
    canSubdivideBeat(beatTime, nextBeat) &&
    random() < difficulty.subdivisionChance
  ) {
    events.push({
      kind: "target",
      trackTime: beatTime + (nextBeat - beatTime) / 2,
    });
  }
  return events;
}

const FLOW_TRANSITIONS: Record<string, CutDirection[]> = {
  right: ["left", "up-left", "down-left"],
  left: ["right", "up-right", "down-right"],
  up: ["down", "down-left", "down-right"],
  down: ["up", "up-left", "up-right"],
  "up-right": ["down-left", "left", "down"],
  "up-left": ["down-right", "right", "down"],
  "down-right": ["up-left", "left", "up"],
  "down-left": ["up-right", "right", "up"],
};

const TARGET_POSITIONS = [
  { laneX: -0.55, height: 1.25 },
  { laneX: 0, height: 1.25 },
  { laneX: 0.55, height: 1.25 },
  { laneX: -0.55, height: 1.58 },
  { laneX: 0, height: 1.58 },
  { laneX: 0.55, height: 1.58 },
];

function pick<T>(values: T[], random: () => number): T {
  return values[
    Math.min(values.length - 1, Math.floor(random() * values.length))
  ];
}

export function generateSurvivalNote(
  id: number,
  hitTime: number,
  difficulty: SurvivalDifficulty,
  previous: SurvivalCompositionState | null,
  random: () => number = Math.random,
): { note: Note & { id: number }; composition: SurvivalCompositionState } {
  const natural = previous
    ? (FLOW_TRANSITIONS[previous.direction] ?? []).filter((direction) =>
        difficulty.allowedDirections.includes(direction),
      )
    : [];
  const direction = pick(
    natural.length > 0 ? natural : difficulty.allowedDirections,
    random,
  );

  const availablePositions = TARGET_POSITIONS.map((_, index) => index).filter(
    (index) => index !== previous?.positionKey,
  );
  const positionKey = pick(availablePositions, random);
  const position = TARGET_POSITIONS[positionKey];
  const isFast = random() < difficulty.fastChance;
  const appearance: TargetAppearance = isFast ? "fast" : "standard";
  // Precision (small blue) and reaction (medium fast orange) are separate
  // challenges. Their combination is reserved for a rare late-run spike.
  const smallChance = isFast
    ? difficulty.smallFastChance
    : difficulty.smallChance;
  const size: TargetSize = random() < smallChance ? "small" : difficulty.size;
  const speed: TargetSpeed = isFast ? "fast" : difficulty.speed;

  return {
    note: {
      id,
      t: hitTime,
      laneX: position.laneX,
      height: position.height,
      dir: direction,
      // Survival is authored around one blade and a two-handed grip. "any"
      // also keeps recognition identical in single- and dual-controller setups.
      hand: "any",
      size,
      speed,
      appearance,
    },
    composition: { direction, positionKey },
  };
}

const OBSTACLE_TYPES: ObstacleType[] = ["dodge-left", "dodge-right", "duck"];

export function pickSurvivalObstacle(
  random: () => number = Math.random,
): ObstacleType {
  return pick(OBSTACLE_TYPES, random);
}

export function canSubdivideBeat(start: number, end: number): boolean {
  return (end - start) / 2 >= SURVIVAL_MIN_GAP_SECONDS;
}

export function waveNumber(elapsedSeconds: number): number {
  return Math.max(1, Math.floor(elapsedSeconds / 20) + 1);
}

export interface PausableClock {
  now: () => number;
  start: () => void;
  pause: () => void;
  resume: () => void;
}

export function createPausableClock(): PausableClock {
  let startedAt: number | null = null;
  let pausedAt: number | null = null;
  let pausedTotal = 0;

  return {
    now: () => {
      if (startedAt === null) return 0;
      const end = pausedAt ?? performance.now();
      return (end - startedAt - pausedTotal) / 1000;
    },
    start: () => {
      startedAt = performance.now();
      pausedAt = null;
      pausedTotal = 0;
    },
    pause: () => {
      if (startedAt !== null && pausedAt === null) pausedAt = performance.now();
    },
    resume: () => {
      if (pausedAt !== null) {
        pausedTotal += performance.now() - pausedAt;
        pausedAt = null;
      }
    },
  };
}
